import React, { useEffect, useRef, useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Kpis, Panel, Segments, Empty, Modal, useFlash } from '../../ui/kit.js';
import { Done } from '../../ui/actions/parts.js';
import { day } from '../../state/building.js';

import DeepTissue from '../../media/images/spa-screen/deep-tissue-massage.jpg';
import Facial from '../../media/images/spa-screen/facial-massage.jpg';
import SteakFrites from '../../media/images/restaurant-screen/steak-frites.jpg';
import LambChops from '../../media/images/restaurant-screen/lamb-chops.jpg';
import GuestSuite from '../../media/images/modules/guest-suite.jpg';
import WineTasting from '../../media/images/modules/event-wine.jpg';
import PoolCabana from '../../media/images/modules/pool-cabana.jpg';
import Detailing from '../../media/images/car-gr86.jpg';

const SEED = [
    { id: 1, month: 'This month', item: 'Spa: deep tissue massage, 60 min', date: day(-3), amount: 185, paid: false, image: DeepTissue },
    { id: 2, month: 'This month', item: 'Azul Terrace restaurant, dinner for 4', date: day(-6), amount: 412.5, paid: false, image: SteakFrites },
    { id: 3, month: 'This month', item: 'Guest suite, 2 nights', date: day(-9), amount: 350, paid: false, image: GuestSuite },
    { id: 4, month: 'This month', item: 'Valet detailing, full interior', date: day(-11), amount: 145, paid: false, image: Detailing },
    { id: 5, month: 'This month', item: 'Wine tasting tickets x2', date: day(-2), amount: 90, paid: false, image: WineTasting },
    { id: 6, month: 'Last month', item: 'Pool cabana rental, Saturday', date: day(-34), amount: 125, paid: true, image: PoolCabana },
    { id: 7, month: 'Last month', item: 'Spa: couples facial', date: day(-38), amount: 290, paid: true, image: Facial },
    { id: 8, month: 'Last month', item: 'Private chef, club room dinner', date: day(-45), amount: 640, paid: true, image: LambChops }
];

const money = n => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/* Amenity Billing: charges to the unit account and paying them off. */
function BillingScreen() {
    const [flash, notify] = useFlash();
    const [charges, setCharges] = useState(SEED);
    const [month, setMonth] = useState('This month');
    const [lastPaid, setLastPaid] = useState({ amount: 1055, date: day(-30) });

    const balance = charges.filter(c => !c.paid).reduce((sum, c) => sum + c.amount, 0);
    const shown = charges.filter(c => c.month === month);
    const monthTotal = charges.filter(c => c.month === 'This month').reduce((sum, c) => sum + c.amount, 0);

    const [paying, setPaying] = useState(false);

    /* Paying used to happen on the first tap, with no total shown and no
       way back. It now opens a sheet that names the charges and the card,
       and pays only when the resident confirms. */
    const pay = () => {
        if (balance === 0) return notify('Your balance is already zero. Nothing to pay.');
        setPaying(true);
    };

    const settle = amount => {
        setCharges(list => list.map(c => ({ ...c, paid: true })));
        setLastPaid({ amount, date: day(0) });
        notify(`Payment of ${money(amount)} received on Visa ending 4821. Thank you.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead
                eyebrow="Amenity billing"
                title="Your account"
                aside={<button type="button" className="btn btn-primary" onClick={pay}>Pay balance</button>}
            />

            <Kpis items={[
                { label: 'Balance due', value: money(balance), tone: balance ? 'warn' : 'ok', note: balance ? 'Due by the 15th' : 'Paid in full' },
                { label: 'This month', value: money(monthTotal), note: 'Amenity charges' },
                { label: 'Last payment', value: money(lastPaid.amount), note: lastPaid.date, tone: 'info' }
            ]} />

            {flash}

            <div className="split">
                <div className="stack">
                    <Segments label="Period" options={['This month', 'Last month']} value={month} onChange={setMonth} />
                    {shown.length === 0 ? <Empty>No charges for this period.</Empty> : (
                        <ul className="rows">
                            {shown.map(c => (
                                <li className="row row--thumb" key={c.id}>
                                    <img className="row__thumb" src={c.image} alt="" loading="lazy" />
                                    <div className="row__main">
                                        <span className="row__title">{c.item}</span>
                                        <span className="row__meta">{c.date}</span>
                                    </div>
                                    <div className="row__end">
                                        <span className="row__title">{money(c.amount)}</span>
                                        <Chip tone={c.paid ? 'ok' : 'warn'}>{c.paid ? 'Paid' : 'Open'}</Chip>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <Panel>
                    <h4>Payment method</h4>
                    <ul className="rows">
                        <li className="row">
                            <div className="row__main">
                                <span className="row__title">Visa ending 4821</span>
                                <span className="row__meta">Alex Rivera · expires 08/28</span>
                            </div>
                            <div className="row__end">
                                <Chip tone="ok">Default</Chip>
                            </div>
                        </li>
                    </ul>
                    <p className="row__meta">Charges post to your unit account and are billed with your monthly statement.</p>
                </Panel>
            </div>

            <PaySheet
                open={paying}
                charges={charges.filter(c => !c.paid)}
                onClose={() => setPaying(false)}
                onPaid={settle}
            />
        </div>
    );
}

/* Confirm, authorize, receipt. The open charges are captured when the
   sheet opens, so the list does not empty itself under the tick. */
function PaySheet({ open, charges, onClose, onPaid }) {
    const [stage, setStage] = useState('confirm');
    const [items, setItems] = useState(charges);
    const [receipt, setReceipt] = useState('');
    const [wasOpen, setWasOpen] = useState(open);
    const timer = useRef(null);

    /* Reset on opening only: `charges` changes once the payment lands. */
    if (open !== wasOpen) {
        setWasOpen(open);
        if (open) {
            setStage('confirm');
            setItems(charges);
        }
    }

    useEffect(() => () => clearTimeout(timer.current), []);

    const total = items.reduce((sum, c) => sum + c.amount, 0);

    const confirm = () => {
        setStage('authorizing');
        timer.current = setTimeout(() => {
            setReceipt(`R-${String(Date.now()).slice(-6)}`);
            setStage('done');
            onPaid(total);
        }, 1400);
    };

    const actions = stage === 'done'
        ? <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
        : (
            <>
                <button type="button" className="btn btn-outline-light" onClick={onClose} disabled={stage === 'authorizing'}>Not now</button>
                <button type="button" className="btn btn-primary" onClick={confirm} disabled={stage === 'authorizing'}>
                    {stage === 'authorizing' ? 'Authorizing…' : `Pay ${money(total)}`}
                </button>
            </>
        );

    return (
        <Modal
            open={open}
            onClose={onClose}
            dismissable={stage !== 'authorizing'}
            eyebrow="Amenity billing"
            title={stage === 'done' ? 'Payment received' : 'Pay your balance'}
            actions={actions}
        >
            {stage === 'done' ? (
                <Done title={`${money(total)} paid`}>
                    <p>
                        Charged to Visa ending 4821 on {day(0)}. Receipt {receipt} is in your statements,
                        and a copy is on its way to your email.
                    </p>
                </Done>
            ) : (
                <>
                    <ul className="rows">
                        {items.map(c => (
                            <li className="row" key={c.id}>
                                <div className="row__main">
                                    <span className="row__title">{c.item}</span>
                                    <span className="row__meta">{c.date}</span>
                                </div>
                                <div className="row__end">
                                    <span className="row__title">{money(c.amount)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <dl className="order-sum">
                        <div className="order-sum__total"><dt>Total</dt><dd>{money(total)}</dd></div>
                        <div><dt>Card</dt><dd>Visa ending 4821</dd></div>
                    </dl>
                    {stage === 'authorizing' && (
                        <p className="pay-wait" role="status">
                            <span className="pay-wait__spin" aria-hidden="true" />
                            Authorizing with your card issuer
                        </p>
                    )}
                </>
            )}
        </Modal>
    );
}

export default BillingScreen;
